/*
  <video-carousel> — Glow-Getter Video Carousel ("From the chef").

  Mounts Splide (already loaded globally by layout/theme.liquid) on the
  .splide root inside the element and handles video playback:
  - Mouse / trackpad: hovering a card plays its video; leaving PAUSES it in
    place, so hovering again resumes from the same spot. The cover image is
    only shown until the video plays for the first time.
  - Touch: tapping a card toggles play / pause.
  - Every card has a play/pause button and a sound button (keyboard users
    can't hover). Sound is per card and starts muted — browsers only allow
    unmuted playback after a real click, so if an unmuted hover-play is
    refused the card falls back to muted and the sound button says so.
  - Only one video plays at a time; moving the carousel pauses everything.
  - In the theme editor, selecting a Video block scrolls the carousel to it.

  Button labels come from data-label-* attributes on the element (Liquid
  translations) — no text lives in this file.

  Deliberately NOT using the theme's `.has-carousel` auto-init (base.js /
  theme-editor.js): that path never exposes the Splide instance, which we
  need for pause-on-move and for destroy() on disconnect, and theme-editor.js
  re-mounts every `.has-carousel` on the page whenever any section reloads.

  Slide counts per breakpoint live here; the peek widths and gap are CSS
  variables in section-video-carousel.css so they can be tuned without JS.
*/

if (!customElements.get('video-carousel')) {
  customElements.define(
    'video-carousel',
    class VideoCarousel extends HTMLElement {
      static get splideOptions() {
        return {
          type: 'slide',
          perPage: 4,
          perMove: 1,
          gap: 'var(--gg-vc-gap)',
          // Strings pass straight through to the track's inline style, so
          // card 1 aligns with the site margin and the next card peeks in
          // by --gg-vc-peek (see the CSS header comment).
          padding: {
            left: 'var(--theme-layout-margin, 0px)',
            right: 'var(--gg-vc-peek)',
          },
          arrows: true,
          pagination: false,
          rewind: false,
          speed: 500,
          // Splide breakpoints are max-width.
          breakpoints: {
            1023: { perPage: 3 },
            767: { perPage: 1 },
          },
        };
      }

      connectedCallback() {
        this.root = this.querySelector('.splide');
        if (!this.root) return;

        if (typeof window.Splide === 'undefined') {
          console.warn('[video-carousel] Splide is not loaded; carousel left static.');
          return;
        }

        this.slides = Array.from(this.querySelectorAll('.section-video-carousel__slide'));
        this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        this.labels = {
          play: this.dataset.labelPlay || '',
          pause: this.dataset.labelPause || '',
          soundOn: this.dataset.labelSoundOn || '',
          soundOff: this.dataset.labelSoundOff || '',
        };

        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onSlideClick = this.onSlideClick.bind(this);
        this.onPlayClick = this.onPlayClick.bind(this);
        this.onSoundClick = this.onSoundClick.bind(this);
        this.pauseAll = this.pauseAll.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);

        this.splide = new window.Splide(this.root, VideoCarousel.splideOptions);
        this.splide.on('move', this.pauseAll);
        this.splide.mount();

        this.slides.forEach((slide) => {
          if (this.canHover) {
            slide.addEventListener('mouseenter', this.onMouseEnter);
            slide.addEventListener('mouseleave', this.onMouseLeave);
          } else {
            slide.addEventListener('click', this.onSlideClick);
          }

          const playButton = slide.querySelector('.section-video-carousel__control--play');
          const soundButton = slide.querySelector('.section-video-carousel__control--sound');
          if (playButton) playButton.addEventListener('click', this.onPlayClick);
          if (soundButton) soundButton.addEventListener('click', this.onSoundClick);
        });

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }
      }

      disconnectedCallback() {
        if (this.slides) {
          this.slides.forEach((slide) => {
            slide.removeEventListener('mouseenter', this.onMouseEnter);
            slide.removeEventListener('mouseleave', this.onMouseLeave);
            slide.removeEventListener('click', this.onSlideClick);

            const playButton = slide.querySelector('.section-video-carousel__control--play');
            const soundButton = slide.querySelector('.section-video-carousel__control--sound');
            if (playButton) playButton.removeEventListener('click', this.onPlayClick);
            if (soundButton) soundButton.removeEventListener('click', this.onSoundClick);
          });
        }

        document.removeEventListener('shopify:block:select', this.onBlockSelect);

        if (this.splide) {
          this.splide.destroy();
          this.splide = null;
        }
      }

      /* ---------- Playback ---------- */

      play(slide) {
        const video = slide.querySelector('video');
        if (!video) return;

        // One video at a time — otherwise two unmuted cards talk over each other.
        this.slides.forEach((other) => {
          if (other !== slide) this.pause(other);
        });

        // play() returns a promise that rejects if the browser blocks it
        // (or the source fails). Unmuted playback without a click is the
        // usual reason: retry muted and reflect that on the sound button.
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt
            .then(() => this.setPlayingState(slide, true))
            .catch(() => {
              if (!video.muted) {
                this.setMuted(slide, true);
                video.play()
                  .then(() => this.setPlayingState(slide, true))
                  .catch(() => this.setPlayingState(slide, false));
              } else {
                this.setPlayingState(slide, false);
              }
            });
        } else {
          this.setPlayingState(slide, true);
        }
      }

      pause(slide) {
        const video = slide.querySelector('video');
        if (video && !video.paused) {
          video.pause();
        }
        this.setPlayingState(slide, false);
      }

      toggle(slide) {
        const video = slide.querySelector('video');
        if (!video) return;

        if (video.paused) {
          this.play(slide);
        } else {
          this.pause(slide);
        }
      }

      pauseAll() {
        this.slides.forEach((slide) => this.pause(slide));
      }

      setPlayingState(slide, isPlaying) {
        slide.classList.toggle('is-playing', isPlaying);
        // The cover stays hidden once the video has started so a paused
        // card shows the frame it stopped on, not the cover again.
        if (isPlaying) slide.classList.add('is-started');

        const playButton = slide.querySelector('.section-video-carousel__control--play');
        if (playButton) {
          playButton.setAttribute('aria-label', isPlaying ? this.labels.pause : this.labels.play);
        }
      }

      setMuted(slide, isMuted) {
        const video = slide.querySelector('video');
        if (video) video.muted = isMuted;

        const soundButton = slide.querySelector('.section-video-carousel__control--sound');
        if (soundButton) {
          soundButton.setAttribute('aria-pressed', isMuted ? 'false' : 'true');
          soundButton.setAttribute('aria-label', isMuted ? this.labels.soundOn : this.labels.soundOff);
        }
      }

      /* ---------- Events ---------- */

      onMouseEnter(event) {
        this.play(event.currentTarget);
      }

      onMouseLeave(event) {
        this.pause(event.currentTarget);
      }

      onSlideClick(event) {
        // Splide cancels the click that ends a drag, so a swipe never
        // toggles playback; only a genuine tap reaches here.
        if (event.defaultPrevented) return;
        this.toggle(event.currentTarget);
      }

      onPlayClick(event) {
        event.stopPropagation(); // don't let the card's tap handler toggle it back
        this.toggle(event.currentTarget.closest('.section-video-carousel__slide'));
      }

      onSoundClick(event) {
        event.stopPropagation();
        const slide = event.currentTarget.closest('.section-video-carousel__slide');
        const video = slide.querySelector('video');
        if (!video) return;

        this.setMuted(slide, !video.muted);

        // Turning sound on is a click, so the browser allows unmuted play —
        // start the card if it isn't already running.
        if (!video.muted && video.paused) {
          this.play(slide);
        }
      }

      onBlockSelect(event) {
        const index = this.slides.indexOf(event.target);
        if (index > -1) {
          this.splide.go(index);
        }
      }
    },
  );
}
